import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { ChevronDown, Map, MapPin, Layers, Eye, EyeOff, Upload, Search, Trash2, Plus, Square, Users, Navigation, Container, RotateCw, Zap, Grid3x3 } from "lucide-react";

// Simpel Dropdown Menu med Portal
function Dropdown({ label, icon: Icon, children, isActive }) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (buttonRef.current && !buttonRef.current.contains(e.target) &&
          menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
  }, [isOpen]);

  return (
    <>
      <div ref={buttonRef}>
        <Button
          variant={isActive ? "default" : "secondary"}
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          className={`text-xs uppercase ${isActive ? 'bg-primary text-primary-foreground' : ''}`}
        >
          {Icon && <Icon className="h-4 w-4 mr-2" />}
          {label}
          <ChevronDown className={`h-3 w-3 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </Button>
      </div>
      
      {isOpen && createPortal(
        <div 
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            minWidth: '220px',
            backgroundColor: '#111111',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '8px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
            zIndex: 99999,
            padding: '8px 0'
          }}
        >
          {children}
        </div>,
        document.body
      )}
    </>
  );
}

// Menu Item
function Item({ icon: Icon, label, onClick, active, danger, disabled }) {
  return (
    <button
      onClick={() => {
        if (!disabled && onClick) onClick();
      }}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        padding: '10px 16px',
        border: 'none',
        background: active ? 'rgba(225, 255, 47, 0.1)' : 'transparent',
        color: danger ? '#f87171' : active ? '#e1ff2f' : '#ffffff',
        fontSize: '14px',
        textAlign: 'left',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 0.15s'
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = danger ? 'rgba(248,113,113,0.1)' : 'rgba(255,255,255,0.08)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = active ? 'rgba(225, 255, 47, 0.1)' : 'transparent';
      }}
    >
      {Icon && <Icon style={{ width: 16, height: 16, flexShrink: 0 }} />}
      <span style={{ flex: 1 }}>{label}</span>
      {active && <span style={{ color: '#e1ff2f' }}>✓</span>}
    </button>
  );
}

// Divider
function Divider() {
  return <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', margin: '6px 0' }} />;
}

// Header
function Header({ children }) {
  return (
    <div style={{ 
      padding: '6px 16px', 
      fontSize: '11px', 
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#888',
      fontFamily: 'monospace'
    }}>
      {children}
    </div>
  );
}

// Main Toolbar
export default function MapToolbar({
  // Kort
  onSearchLocation,
  onUploadMap,
  onToggleOverlay,
  onGeoreference,
  onDeleteOverlay,
  onSaveDefaultLocation,
  onRotateOverlay,
  onOptimizeOverlay,        // (quality) => void
  onRestoreOverlay,         // () => void  (fortryd seneste optimering)
  overlayInfo, // { file_size_mb, uploaded_at, can_restore, optimized_at }
  showOverlay,
  hasOverlay,
  hasDefaultLocation,
  isGeoreferencing,
  overlayRotation,
  onRotationChange,
  onRotationCommit,
  
  // Punkt
  onAddPoint,
  showAddForm,
  onAddMaxicontainer,
  showAddMaxicontainer,
  
  // Zone
  onToggleAreas,
  onToggleWorkZones,
  onToggleCleaningZones,
  showAreas,
  showWorkZones,
  showCleaningZones,
  onDrawArea,
  onDrawWorkZone,
  onDrawCleaningZone,
  onAssignTeams,  // NY: Hold-tildeling til områder
  
  // Maxicontainer visning
  onToggleMaxicontainers,
  showMaxicontainers,

  // Grid overlay
  onToggleGrid,
  showGrid,
  
  // User
  userRole,
  guestCanPlaceAttentionPoints,  // gæst-flag fra visible_layers.attention_points
  canPerformAction,  // Fra PermissionsContext - bruges til granulær gate af opret-handlinger
}) {
  const isAdmin = userRole === "admin";
  const isLeader = userRole === "heart_leader" || userRole === "assisterende_admin";
  const isGuest = userRole === "gæst";
  const canCreateCleaningZone = userRole === "admin" || userRole === "heart_leader";
  // Rengøringszoner: skal kunne SES af alle brugere (frivillige, ledere, admins).
  // Kun gæster har ikke automatisk se-adgang (deres synlighed styres af visible_layers).
  const canSeeCleaningZones = !isGuest;
  // Gæst må KUN se Punktplacering hvis admin har givet lov til opmærksomhedspunkter
  const canShowPointPlacement = !isGuest || !!guestCanPlaceAttentionPoints;
  // Maxicontainer-opret gates via permission (fallback: admin hvis funktion ikke er sendt med)
  const canAddMaxicontainer = canPerformAction ? canPerformAction('maxicontainer_opret') : isAdmin;
  const [showOptimizeChoice, setShowOptimizeChoice] = useState(false);

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      {/* KORT */}
      {isAdmin && (
        <Dropdown label="Kort" icon={Map}>
          <Item icon={Search} label="Søg lokation" onClick={onSearchLocation} disabled={isGeoreferencing} />
          <Item icon={Upload} label="Upload kort" onClick={onUploadMap} disabled={isGeoreferencing} />
          <Divider />
          <Item 
            icon={Navigation} 
            label={hasDefaultLocation ? "Opdater start-lokation" : "Gem som start-lokation"} 
            onClick={onSaveDefaultLocation} 
            disabled={isGeoreferencing}
          />
          
          {hasOverlay && (
            <>
              <Divider />
              <Item 
                icon={showOverlay ? EyeOff : Eye} 
                label={showOverlay ? "Skjul kort" : "Vis kort"} 
                onClick={onToggleOverlay}
                disabled={isGeoreferencing}
              />
              {showOverlay && onToggleGrid && (
                <Item
                  icon={Grid3x3}
                  label={showGrid ? "Skjul rutenet" : "Vis rutenet (A1..E5)"}
                  onClick={onToggleGrid}
                  disabled={isGeoreferencing}
                />
              )}
              <Item icon={MapPin} label="Georeferér (2+ pkt)" onClick={onGeoreference} disabled={isGeoreferencing} />
              <Item icon={RotateCw} label="Rotér billede" onClick={onRotateOverlay} disabled={isGeoreferencing} />
              {!showOptimizeChoice ? (
                <Item icon={Zap} label="Optimér (hurtigere kort)" onClick={() => setShowOptimizeChoice(true)} disabled={isGeoreferencing} />
              ) : (
                <div style={{ padding: '6px 16px 8px', background: 'rgba(255,255,255,0.04)' }}>
                  <div style={{ fontSize: '11px', color: '#888', marginBottom: 6, fontFamily: 'monospace', textTransform: 'uppercase' }}>
                    Vælg komprimering
                  </div>
                  {[
                    { q: 92, maxDim: 5000, label: 'Let komprimering', sub: 'Bedst tekst-læsbarhed (max 5000px)' },
                    { q: 85, maxDim: 4500, label: 'Medium', sub: 'God balance (max 4500px)' },
                    { q: 78, maxDim: 4000, label: 'Maks komprimering', sub: 'Mindste fil — hurtigste pan (max 4000px)' },
                  ].map(({ q, maxDim, label, sub }) => (
                    <button
                      key={q}
                      onClick={() => { setShowOptimizeChoice(false); onOptimizeOverlay && onOptimizeOverlay(q, maxDim); }}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '6px 8px', marginBottom: 4, borderRadius: 4,
                        border: '1px solid rgba(255,255,255,0.1)', background: 'transparent',
                        color: '#fff', cursor: 'pointer'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(225,255,47,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      data-testid={`optimize-quality-${q}`}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
                      <div style={{ fontSize: 10, color: '#888' }}>{sub}</div>
                    </button>
                  ))}
                  <button
                    onClick={() => setShowOptimizeChoice(false)}
                    style={{
                      width: '100%', padding: '4px', fontSize: 10, color: '#888',
                      background: 'transparent', border: 'none', cursor: 'pointer'
                    }}
                  >
                    Annuller
                  </button>
                </div>
              )}
              {overlayInfo && overlayInfo.can_restore && onRestoreOverlay && (
                <Item icon={RotateCw} label="Fortryd optimering" onClick={onRestoreOverlay} />
              )}
              <Divider />
              <Item icon={Trash2} label="Slet kort" onClick={onDeleteOverlay} danger disabled={isGeoreferencing} />
            </>
          )}
        </Dropdown>
      )}

      {/* PUNKTPLACERING - skjult for gæster medmindre admin har givet lov */}
      {canShowPointPlacement && (
      <Dropdown label="Punktplacering" icon={MapPin} isActive={showAddForm || showAddMaxicontainer}>
        <Item icon={Plus} label={isGuest ? "Placér opmærksomhedspunkt" : "Placér nyt punkt"} onClick={onAddPoint} active={showAddForm} />
        {canAddMaxicontainer && (
          <Item icon={Container} label="Tilføj Maxicontainer" onClick={onAddMaxicontainer} active={showAddMaxicontainer} />
        )}
      </Dropdown>
      )}

      {/* ZONE */}
      <Dropdown label="Zone" icon={Layers}>
        <Header>Vis / skjul</Header>
        <Item icon={Layers} label="Områder" onClick={onToggleAreas} active={showAreas} />
        <Item icon={Users} label="Arbejdszoner" onClick={onToggleWorkZones} active={showWorkZones} />
        {/* Rengøringszoner - kun synlig for ledere og admin */}
        {canSeeCleaningZones && (
          <Item icon={Square} label="Rengøringszoner" onClick={onToggleCleaningZones} active={showCleaningZones} />
        )}
        
        {!isGuest && (isAdmin || canCreateCleaningZone) && (
          <>
            <Divider />
            <Header>Tegn ny zone</Header>
            {isAdmin && (
              <>
                <Item icon={Layers} label="Tegn Område" onClick={onDrawArea} />
                <Item icon={Users} label="Tegn Arbejdszone" onClick={onDrawWorkZone} />
              </>
            )}
            {canCreateCleaningZone && (
              <Item icon={Square} label="Tegn Rengøringszone" onClick={onDrawCleaningZone} />
            )}
          </>
        )}
      </Dropdown>
    </div>
  );
}
